import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileWarning,
  Loader2,
} from "lucide-react";
import {
  useReportReadiness,
  type CardReadiness,
} from "@/hooks/useReportReadiness";
import { FIELD_DEFINITIONS } from "@shared/field-definitions";
import { cn } from "@/lib/utils";

interface ReportReadinessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
  onProceed: (narrative: string | null) => void;
  isProceedLoading: boolean;
}

export function ReportReadinessModal({
  open,
  onOpenChange,
  assessmentId,
  onProceed,
  isProceedLoading,
}: ReportReadinessModalProps) {
  const [narrative, setNarrative] = useState("");
  const [certified, setCertified] = useState(false);

  const {
    data: readiness,
    isLoading,
    error,
  } = useReportReadiness(assessmentId, open);

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Checking report readiness...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !readiness) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error Loading Readiness
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Unable to load the readiness report. Please try again.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    basicModuleComplete,
    comprehensiveModuleComplete,
    basicMissingCount,
    comprehensiveMissingCount,
  } = readiness;

  const cardOrder = new Map(FIELD_DEFINITIONS.map((def, i) => [def.cardId, i]));
  const cards = [...readiness.cards].sort(
    (a, b) => (cardOrder.get(a.cardId) ?? 999) - (cardOrder.get(b.cardId) ?? 999)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileWarning className="h-5 w-5 text-amber-500" />
            Report Readiness Check
          </DialogTitle>
          <DialogDescription>
            Review your assessment completeness before generating the report.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Module Status */}
          <div
            className={cn(
              "p-4 rounded-lg border-2",
              basicModuleComplete
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            )}
          >
            <div className="flex items-center gap-3">
              {basicModuleComplete ? (
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold">Basic Module (VSME Basic)</h3>
                {basicModuleComplete ? (
                  <p className="text-sm text-green-700">All required fields are complete.</p>
                ) : (
                  <p className="text-sm text-red-700">
                    {basicMissingCount} required field{basicMissingCount !== 1 ? "s" : ""} missing.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Comprehensive Module Status */}
          <div
            className={cn(
              "p-4 rounded-lg border",
              comprehensiveModuleComplete
                ? "bg-green-50 border-green-200"
                : "bg-amber-50 border-amber-200"
            )}
          >
            <div className="flex items-center gap-3">
              {comprehensiveModuleComplete ? (
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold">Comprehensive Module (VSME Comprehensive)</h3>
                {comprehensiveModuleComplete ? (
                  <p className="text-sm text-green-700">All optional fields are complete.</p>
                ) : (
                  <p className="text-sm text-amber-700">
                    {comprehensiveMissingCount} optional field{comprehensiveMissingCount !== 1 ? "s" : ""} not yet filled.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Gap Report */}
          {cards.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                {basicModuleComplete && !comprehensiveModuleComplete
                  ? "Optional Fields"
                  : "Gap Report"}
              </h3>
              {cards.map((card) => (
                <CardGapSection key={card.cardId} card={card} />
              ))}
            </div>
          )}

          {/* Narrative justification */}
          <div className="space-y-2">
            <Label htmlFor="report-narrative" className="text-sm font-medium">
              Justification for unreported fields{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="report-narrative"
              placeholder="Explain why certain fields were not reported..."
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              className="min-h-[96px] resize-y"
            />
          </div>

          {/* Certification checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="certify"
              checked={certified}
              onCheckedChange={(checked) => setCertified(checked === true)}
              className="mt-0.5"
            />
            <Label htmlFor="certify" className="text-sm leading-snug cursor-pointer">
              I certify that the information provided is accurate and complete to the
              best of my knowledge.
            </Label>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {basicModuleComplete ? (
            <Button
              onClick={() => onProceed(narrative.trim() || null)}
              disabled={!certified || isProceedLoading}
              className="bg-[#00444C] hover:bg-[#001F24]"
            >
              {isProceedLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : comprehensiveModuleComplete ? (
                "Generate Comprehensive Report"
              ) : (
                "Generate Basic Report"
              )}
            </Button>
          ) : (
            <Button disabled className="bg-gray-400 cursor-not-allowed">
              Complete Required Fields First
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CardGapSection({ card }: { card: CardReadiness }) {
  const [expanded, setExpanded] = useState(false);

  const tabColors: Record<string, string> = {
    general: "text-purple-600 bg-purple-50",
    environment: "text-blue-600 bg-blue-50",
    social: "text-pink-600 bg-pink-50",
    governance: "text-indigo-600 bg-indigo-50",
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "text-xs font-medium uppercase px-2 py-0.5 rounded",
              tabColors[card.tab] || "text-gray-600 bg-gray-50"
            )}
          >
            {card.tab}
          </span>
          <span className="font-medium">{card.cardName}</span>
        </div>
        <div className="flex items-center gap-2">
          {card.basicMissing.length > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
              {card.basicMissing.length} required
            </span>
          )}
          {card.comprehensiveMissing.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
              {card.comprehensiveMissing.length} optional
            </span>
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t bg-muted/20">
          {card.basicMissing.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-medium text-red-600 mb-1">Required fields</p>
              <ul className="text-sm space-y-1">
                {card.basicMissing.map((field) => (
                  <li
                    key={field.fieldKey}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    {field.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {card.comprehensiveMissing.length > 0 && (
            <div>
              <p className="text-xs font-medium text-amber-600 mb-1">Optional fields</p>
              <ul className="text-sm space-y-1">
                {card.comprehensiveMissing.map((field) => (
                  <li
                    key={field.fieldKey}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    {field.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
